import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useFeedback } from "../components/Feedback";
import { Card, ErrorBox, Loading, PageHeader } from "../components/Page";
import { api, type DocDetail, type DocLine, type DocType, type Party } from "../lib/api";
import { money, num } from "../lib/format";
import { useApi } from "../lib/useApi";
import { DOC_TYPES } from "./Documents";
import { PurchaseOrderGuide, type GuideItem } from "./PurchaseOrderGuide";

export interface Item { sku: string; name: string; size_ml: number | null; units_per_case: number | null; proof: number | null; product_class: string }
export interface Form {
  doc_type: DocType; number: string; issue_date: string; ship_date: string; required_date: string;
  issuer_id: string; bill_to_id: string; ship_to_id: string; consignee_id: string; vendor_id: string;
  customer_id: string; channel: string; from_location: string; to_location: string;
  customer_po: string; booking_no: string; ship_via: string; origin: string; pallets: string; weight_lbs: string;
  banner: string; bottled_by: string; remarks: string; notes: string; currency: string; practice: boolean;
  header_fields: { label: string; value: string }[];
  lines: { sku: string; description: string; qty: string; uom: string; unit_price: string }[];
}

const s = (v: unknown) => (v == null ? "" : String(v));
const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "XCD"];

function toForm(d: Partial<DocDetail> & { doc_type: DocType }): Form {
  return {
    doc_type: d.doc_type, number: s(d.number), issue_date: s(d.issue_date), ship_date: s(d.ship_date), required_date: s(d.required_date),
    issuer_id: s(d.issuer_id), bill_to_id: s(d.bill_to_id), ship_to_id: s(d.ship_to_id), consignee_id: s(d.consignee_id), vendor_id: s(d.vendor_id),
    customer_id: s(d.customer?.id ?? (d as { customer_id?: number }).customer_id), channel: s(d.channel),
    from_location: s(d.from_location), to_location: s(d.to_location),
    customer_po: s(d.customer_po), booking_no: s(d.booking_no), ship_via: s(d.ship_via), origin: s(d.origin),
    pallets: s(d.pallets), weight_lbs: s(d.weight_lbs), banner: s(d.banner), bottled_by: s(d.bottled_by),
    remarks: s(d.remarks), notes: s(d.notes), currency: s(d.currency) || "USD", header_fields: d.header_fields ?? [],
    practice: Boolean(d.practice),
    lines: (d.lines ?? []).map((l: DocLine) => ({ sku: s(l.sku), description: l.description, qty: s(l.qty), uom: l.uom, unit_price: s(l.unit_price) })),
  };
}

/** "750ml/6 Shipwreck Coconut Rum (36% Alc. By Vol.)" — the way Zach writes it. */
export function describe(it: Item): string {
  if (!it.size_ml) return it.name;
  const base = it.name.replace(/\s*\d+ml$/i, "");
  const abv = it.proof ? ` (${Math.round(it.proof / 2)}% Alc. By Vol.)` : "";
  return `${it.size_ml}ml/${it.units_per_case ?? ""} ${base}${abv}`;
}

export function DocumentForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const type = (params.get("type") ?? "sales_invoice") as DocType;
  const nav = useNavigate();
  const { toast } = useFeedback();
  // A new document may arrive prefilled (the new design's Home: "Open the full form" from its
  // Transfer / PO dialog); those values go over the usual defaults. Nothing else changes.
  const prefill = (useLocation().state as { prefill?: Partial<Form> } | null)?.prefill;

  const source = useApi<DocDetail>(id ? `/documents/${id}` : `/documents/new?doc_type=${type}`);
  const parties = useApi<Party[]>("/parties");
  const items = useApi<Item[]>("/items");
  const locations = useApi<{ code: string; name: string }[]>("/locations");
  const channels = useApi<{ code: string; name: string }[]>("/channels");
  const customers = useApi<{ id: number; name: string; channel_name: string }[]>("/customers");

  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (source.data) setForm({ ...toForm(source.data), ...(!id && prefill ? prefill : {}),
                               ...(!id && params.get("practice") ? { practice: true } : {}) });
  }, [source.data, id, params, prefill]);

  const totals = useMemo(() => {
    const lines = (form?.lines ?? []).map((l) => Math.round((Number(l.qty) || 0) * (Number(l.unit_price) || 0) * 100) / 100);
    return { lines, qty: (form?.lines ?? []).reduce((a, l) => a + (Number(l.qty) || 0), 0), amount: lines.reduce((a, b) => a + b, 0) };
  }, [form?.lines]);

  if (source.error) return <ErrorBox error={source.error} onRetry={source.reload} />;
  if (!form || !parties.data || !items.data) return <Loading />;
  if (id && source.data && !source.data.editable) {
    return <ErrorBox error={`${source.data.type_label} ${source.data.number} is ${source.data.status} and can no longer be edited. Duplicate it to make a new draft.`} />;
  }

  const t = form.doc_type;
  const label = DOC_TYPES.find((d) => d.id === t)!.label;
  const set = (k: keyof Form, v: unknown) => setForm((f) => (f ? { ...f, [k]: v } : f));
  const setLine = (i: number, patch: Partial<Form["lines"][number]>) =>
    setForm((f) => (f ? { ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) } : f));
  const pickItem = (i: number, sku: string) => {
    const it = items.data!.find((x) => x.sku === sku);
    setLine(i, { sku, ...(it ? { description: describe(it), uom: t === "purchase_order" && it.product_class !== "finished" ? "units" : "cases" } : {}) });
  };
  const addLine = () => set("lines", [...form.lines, { sku: "", description: "", qty: "", uom: t === "purchase_order" ? "units" : "cases", unit_price: "" }]);
  // A line from the supplier's own documents: their description and code, the
  // quantity they last shipped, and their price for one unit. The order takes
  // the currency they bill in; one order is never in two currencies.
  const addFromGuide = (it: GuideItem) => {
    if (form.lines.length && it.currency !== form.currency) {
      toast(`This order is in ${form.currency} and that price is in ${it.currency}. Start a separate order, or change the currency first.`, "error");
      return;
    }
    const description = it.item_code ? `${it.description} (their code ${it.item_code})` : it.description;
    setForm((f) => (f ? {
      ...f, currency: f.lines.length ? f.currency : it.currency,
      lines: [...f.lines, { sku: "", description, qty: it.qty == null ? "" : String(it.qty), uom: "units", unit_price: it.unit_price == null ? "" : String(it.unit_price) }],
    } : f));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const n = (v: string) => (v === "" ? null : Number(v));
    const body = {
      ...form,
      ...Object.fromEntries(["issuer_id", "bill_to_id", "ship_to_id", "consignee_id", "vendor_id", "customer_id", "pallets", "weight_lbs"]
        .map((k) => [k, n(form[k as keyof Form] as string)])),
      ...Object.fromEntries(["issue_date", "ship_date", "required_date", "channel", "from_location", "to_location", "customer_po",
        "booking_no", "ship_via", "origin", "banner", "bottled_by", "remarks", "notes"].map((k) => [k, (form[k as keyof Form] as string) || null])),
      lines: form.lines.map((l) => ({ sku: l.sku || null, description: l.description, qty: Number(l.qty) || 0, uom: l.uom, unit_price: Number(l.unit_price) || 0 })),
    };
    try {
      const saved = id ? await api.put<DocDetail>(`/documents/${id}`, body) : await api.post<DocDetail>("/documents", body);
      toast(`${label} ${saved.number} saved`);
      nav(`/documents/${saved.id}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const partySelect = (k: keyof Form, text: string, kinds?: string[], also?: (p: Party) => boolean) => (
    <Field label={text}>
      <select value={form[k] as string} onChange={(e) => set(k, e.target.value)} aria-label={text}>
        <option value="">—</option>
        {parties.data!.filter((p) => !kinds || kinds.includes(p.kind) || also?.(p)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </Field>
  );
  const input = (k: keyof Form, text: string, type_ = "text", hint?: string) => (
    <Field label={text} hint={hint}>
      <input type={type_} value={form[k] as string} onChange={(e) => set(k, e.target.value)} aria-label={text} />
    </Field>
  );

  return (
    <>
      <PageHeader crumbs={[{ label: "Documents", to: "/documents" }]}
        title={id ? `Edit ${label.toLowerCase()} ${form.number}` : `New ${form.practice ? "practice " : ""}${label.toLowerCase()}`}
        sub="Saved as a draft. Nothing is sent and no stock changes until you choose to."
        actions={<>
          <Link className="btn" to={id ? `/documents/${id}` : "/documents"}>Cancel</Link>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save draft"}</button>
        </>} />

      {error && <ErrorBox error={error} />}

      {!id && (
        <label className="check practice-check">
          <input type="checkbox" checked={form.practice} onChange={(e) => setForm({ ...form, practice: e.target.checked })} />
          Practice only — marked PRACTICE, uses no real number, never touches stock, can be deleted any time
        </label>
      )}

      <div className="two-up">
        <Card title="Header">
          <div className="form-grid">
            {form.practice
              ? <Field label={t === "purchase_order" ? "PO number" : "Invoice number"} hint="Practice documents are numbered PRACTICE-1, -2…">
                  <input value={id ? form.number : "PRACTICE-…"} disabled aria-label="Number" /></Field>
              : input("number", t === "purchase_order" ? "PO number" : "Invoice number", "text", "Suggested from Settings › Numbering")}
            {input("issue_date", t === "purchase_order" ? "Order date" : "Issue date", "date")}
            {t !== "purchase_order" && input("ship_date", "Ship date", "date")}
            {t === "purchase_order" && input("required_date", "Required ship date", "date")}
            {t === "purchase_order" && (
              <Field label="Currency" hint="Set from the supplier's prices when you add one">
                <select aria-label="Currency" value={form.currency} onChange={(e) => set("currency", e.target.value)}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            )}
            {t === "sales_invoice" && input("customer_po", "Customer PO #")}
            {t !== "sales_invoice" && input("booking_no", "Booking #")}
            {input("ship_via", "Ship via")}
            {t !== "purchase_order" && input("origin", "C/O (country of origin)")}
            {t !== "purchase_order" && input("pallets", "Pallets", "number")}
            {t !== "purchase_order" && input("weight_lbs", "Approx. weight (lbs)", "number")}
          </div>
        </Card>
        <Card title="Parties">
          <div className="form-grid">
            {partySelect("issuer_id", "From (printed top left)", ["own_company"])}
            {t === "sales_invoice" && partySelect("bill_to_id", "Bill to")}
            {/* Every supplier in the COGS contact list, BCI (filed as a warehouse) included. */}
            {t === "purchase_order" && partySelect("vendor_id", "Vendor", ["vendor"], (p) => Boolean(p.reference_ids?.["Contact list name"]))}
            {partySelect("ship_to_id", "Ship to")}
            {t === "transfer_invoice" && partySelect("consignee_id", "Consignee")}
            {t === "transfer_invoice" && (
              <>
                <Field label="From location">
                  <select aria-label="From location" value={form.from_location} onChange={(e) => set("from_location", e.target.value)}>
                    <option value="">—</option>{(locations.data ?? []).map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
                  </select>
                </Field>
                <Field label="To location">
                  <select aria-label="To location" value={form.to_location} onChange={(e) => set("to_location", e.target.value)}>
                    <option value="">—</option>{(locations.data ?? []).map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
                  </select>
                </Field>
              </>
            )}
            {t === "sales_invoice" && (
              <>
                <Field label="Sales channel">
                  <select aria-label="Sales channel" value={form.channel} onChange={(e) => set("channel", e.target.value)}>
                    <option value="">—</option>{(channels.data ?? []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </Field>
                <Field label="Sales customer" hint="Links the invoice to the customer's sales history">
                  <select aria-label="Sales customer" value={form.customer_id} onChange={(e) => set("customer_id", e.target.value)}>
                    <option value="">—</option>{(customers.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.channel_name})</option>)}
                  </select>
                </Field>
              </>
            )}
          </div>
          <p className="note muted">Missing an address? <Link to="/settings/address-book/new">Add it to the address book</Link>.</p>
        </Card>
      </div>

      {t === "purchase_order" && <PurchaseOrderGuide vendorId={form.vendor_id} onAdd={addFromGuide} />}

      <Card title="Lines" actions={<button className="btn btn-sm" onClick={addLine}>+ Add line</button>}>
        <div className="table-scroll">
          <table className="grid line-editor" data-testid="line-editor">
            <thead><tr><th>Product</th><th>Description (printed)</th><th className="n">Qty</th><th>Unit</th><th className="n">Unit price</th><th className="n">Extension</th><th /></tr></thead>
            <tbody>
              {form.lines.length === 0 && <tr><td colSpan={7} className="empty-cell">No lines yet. <button className="link-btn" onClick={addLine}>Add the first line</button></td></tr>}
              {form.lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <select aria-label={`Line ${i + 1} product`} value={l.sku} onChange={(e) => pickItem(i, e.target.value)}>
                      <option value="">(free text)</option>
                      {items.data!.filter((it) => t === "purchase_order" || it.product_class === "finished").map((it) => <option key={it.sku} value={it.sku}>{it.name}</option>)}
                    </select>
                  </td>
                  <td><input aria-label={`Line ${i + 1} description`} value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} className="wide" /></td>
                  <td className="n"><input aria-label={`Line ${i + 1} quantity`} type="number" min="0" step="any" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className="num-in" /></td>
                  <td>
                    <select aria-label={`Line ${i + 1} unit`} value={l.uom} onChange={(e) => setLine(i, { uom: e.target.value })}>
                      <option value="cases">cases</option><option value="units">units</option>
                    </select>
                  </td>
                  <td className="n"><input aria-label={`Line ${i + 1} unit price`} type="number" min="0" step="any" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} className="num-in" /></td>
                  <td className="n" data-testid={`line-${i + 1}-extension`}>{money(totals.lines[i], form.currency)}</td>
                  <td><button className="icon-btn" aria-label={`Remove line ${i + 1}`} onClick={() => set("lines", form.lines.filter((_, j) => j !== i))}>✕</button></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr><td colSpan={2}><strong>Total</strong></td><td className="n"><strong data-testid="total-qty">{num(totals.qty, 2)}</strong></td><td />
                <td /><td className="n"><strong data-testid="total-amount">{money(totals.amount, form.currency)}</strong></td><td /></tr>
            </tfoot>
          </table>
        </div>
      </Card>

      <div className="two-up">
        <Card title="Printed notes">
          <div className="form-grid one">
            {t !== "purchase_order" && input("banner", "Banner", "text", t === "transfer_invoice" ? "e.g. TAX PAID SHIPMENT ; DOOR MOVE" : "e.g. DUTY FREE SALES - CARIBBEAN")}
            {t === "purchase_order" && input("remarks", "Remarks", "text", "e.g. PLEASE EXPEDITE")}
            {t === "sales_invoice" && (
              <Field label="Bottled by"><textarea aria-label="Bottled by" rows={3} value={form.bottled_by} onChange={(e) => set("bottled_by", e.target.value)} /></Field>
            )}
            <Field label="Notes" hint="One note per line; printed as a numbered list">
              <textarea aria-label="Notes" rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </Field>
          </div>
        </Card>
        <Card title="Extra header fields" actions={<button className="btn btn-sm" onClick={() => set("header_fields", [...form.header_fields, { label: "", value: "" }])}>+ Add field</button>}>
          <p className="note muted">Printed in the box under the title — Vendor ID, FDA #, account numbers.</p>
          {form.header_fields.map((h, i) => (
            <div key={i} className="kv-row">
              <input aria-label={`Header field ${i + 1} label`} placeholder="Label" value={h.label}
                onChange={(e) => set("header_fields", form.header_fields.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <input aria-label={`Header field ${i + 1} value`} placeholder="Value" value={h.value}
                onChange={(e) => set("header_fields", form.header_fields.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
              <button className="icon-btn" aria-label={`Remove header field ${i + 1}`} onClick={() => set("header_fields", form.header_fields.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}
        </Card>
      </div>
    </>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}
